import React, { useReducer } from 'react';
import StoreContext from './storeContext';
import StoreReducer from './storeReducer';
import api from '../../api/api';
import {
    GET_STORES,
    CREATE_STORE,
    GET_STORE,
    DELETE_STORE,
    UPDATE_STORE,
    CLEAR_STATE,
    SET_LOADING,
    UPDATE_ALL_STORES,
    UPDATE_ATTRIBUTES_STORES,
    CLEAR_SNACKBARS,
    CLEAR_ERRORS,
    ADD_SUBSCRIPTION,
    GET_SUBSCRIPTIONS,
    ADD_SUBSCRIPTION_CREDITS,
    GET_STORE_INTEGRATIONS_START,
    GET_STORE_INTEGRATIONS_SUCCESS,
    GET_STORE_INTEGRATIONS_ERROR,
    REFRESH_STORE_INTEGRATIONS_START,
    REFRESH_STORE_INTEGRATIONS_SUCCESS,
    REFRESH_STORE_INTEGRATIONS_ERROR,
} from '../types';
import { HEADERS } from '../../constants/headers';
import axios from 'axios';
import { handleError } from '../utils/handleError';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { AWS_S3 } from 'src/constants/crm/aws';
import { ApiV2 } from 'src/api/apiv2';

const StoreState = (props) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const initialState = {
        stores: [],
        allStores: [],
        store: {},
        count: null,
        loading: false,
        error: null,
        success: null,
        subscriptions: [],
        storeIntegrations: [],
        storeIntegrationsLoading: false,
        storeIntegrationsRefreshing: false,
        storeIntegrationsError: null,
        storeIntegrationsRefreshResult: null,
    };

    const [state, dispatch] = useReducer(StoreReducer, initialState);

    const messageAlertFunction = (message) => {
        enqueueSnackbar(t(message), { variant: 'error' });
    };

    const clearErrors = () =>
        setTimeout(() => dispatch({ type: CLEAR_ERRORS }), 3000);

    const getSubscriptions = async (storeId) => {
        setLoading();
        try {
            const res = await ApiV2.get(`/subscriptions/${storeId}`, HEADERS());

            dispatch({ type: GET_SUBSCRIPTIONS, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error, messageAlertFunction));
        }
    };

    const addSubscription = async (values) => {
        setLoading();
        try {
            const res = await ApiV2.post(
                `/subscriptions`,
                { ...values },
                HEADERS()
            );
            enqueueSnackbar(t('Plans.SubscriptionActivaded'), {
                variant: 'success',
            });
            dispatch({ type: ADD_SUBSCRIPTION, payload: res.data.data });
            setSnackbarInterval();
        } catch (error) {
            dispatch(handleError(error, messageAlertFunction));
        }
    };

    const addSubscriptionCredits = async (subscriptionId, values) => {
        setLoading();
        try {
            const res = await ApiV2.put(
                `subscriptions/credits/${subscriptionId}`,
                { ...values },
                HEADERS()
            );
            enqueueSnackbar(t('Plans.CreditsAdded'), {
                variant: 'success',
            });
            dispatch({
                type: ADD_SUBSCRIPTION_CREDITS,
                payload: res.data.data,
            });
            setSnackbarInterval();
        } catch (error) {
            dispatch(handleError(error, messageAlertFunction));
        }
    };

    const searchStoresByFilters = async (filters) => {
        setLoading();
        try {
            const res = await api.post(
                `/stores/search`,
                { ...filters },
                HEADERS()
            );

            dispatch({
                type: GET_STORES,
                payload: res.data.results,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
            clearErrors();
        }
    };

    const setCreditCount = async (storeId, data) => {
        setLoading();
        try {
            const res = await api.put(
                `/stores/bc-score/${storeId}`,
                { ...data },
                HEADERS()
            );
            dispatch({ type: UPDATE_STORE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getStores = async (query = {}) => {
        clearStoreState();
        setLoading();
        try {
            const res = await api.post(
                `/stores/searchDocuments`,
                query,
                HEADERS()
            );
            dispatch({
                type: GET_STORES,
                payload: res.data.results,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const updateAllStores = async (data) =>
        dispatch({ type: UPDATE_ALL_STORES, payload: { ...data } });

    const updateAttStores = async (value) => {
        dispatch({ type: UPDATE_ATTRIBUTES_STORES, payload: value });
    };

    const getStoreByConversation = async (credentials) => {
        setLoading();
        try {
            const res = await api.get(
                `/stores/conversation/${credentials}`,
                HEADERS()
            );
            dispatch({ type: GET_STORE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getStoreByConversationBDC = async (credentials) => {
        setLoading();
        try {
            const res = await api.get(
                `/stores/conversationBDC/${credentials}`,
                HEADERS()
            );
            dispatch({ type: GET_STORE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getStore = async (storeId) => {
        setLoading();
        try {
            const res = await api.get(`/stores/${storeId}`, HEADERS());
            dispatch({ type: GET_STORE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const createStore = async (store) => {
        clearStoreState();
        setLoading();
        try {
            const res = await api.post(`/stores`, { ...store }, HEADERS());
            dispatch({ type: CREATE_STORE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const deleteStore = async (storeId) => {
        setLoading();
        try {
            const res = await api.delete(`/stores/${storeId}`, HEADERS());
            dispatch({ type: DELETE_STORE, payload: res.data.deletedId });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const updateStore = async (store, storeId, file) => {
        if (file) {
            let uploadConfig = await api.post(
                '/uploads/image',
                { type: file.type, fileName: file.name },
                HEADERS()
            );

            await axios.put(uploadConfig.data.url, file, {
                headers: { 'Content-Type': file ? file.type : null },
            });

            const dataKey = uploadConfig.data.key;

            if (store.hasOwnProperty('videoMailing')) {
                store.videoMailing.imageVideo = dataKey;
            } else {
                store.image = AWS_S3 + dataKey;
            }
        }
        setLoading();
        try {
            const res = await api.put(
                `/stores/${storeId}`,
                { ...store },
                HEADERS()
            );
            dispatch({
                type: UPDATE_STORE,
                payload: res.data.data,
                successMessage: 'SnackBar.StoreUpdated',
            });
            enqueueSnackbar(t('SnackBar.StoreUpdated'), {
                variant: 'success',
            });
            setSnackbarInterval();
            return res.data.data;
        } catch (error) {
            setSnackbarInterval();
            dispatch(handleError(error, messageAlertFunction));
            return null;
        }
    };
    const updateCarouselAgents = async (store) => {
        setLoading();
        try {
            const res = await api.post(
                `/stores/updateCarousel `,
                { ...store },
                HEADERS()
            );
            dispatch({
                type: UPDATE_STORE,
                payload: res.data.data,
                successMessage: 'SnackBar.UpdateCarousel',
            });
            setSnackbarInterval();
        } catch (error) {
            setSnackbarInterval();
            dispatch(handleError(error));
        }
    };

    const getStoreIntegrations = async () => {
        dispatch({ type: GET_STORE_INTEGRATIONS_START });
        try {
            const response = await api.get(
                '/stores/integrations',
                HEADERS({ isReadPermissionNecessary: true })
            );
            dispatch({
                type: GET_STORE_INTEGRATIONS_SUCCESS,
                payload: response?.data?.data || [],
            });
        } catch (error) {
            const errorAction = handleError(error, messageAlertFunction);
            dispatch({
                type: GET_STORE_INTEGRATIONS_ERROR,
                payload: errorAction.payload,
            });
        }
    };

    const refreshStoreIntegrationExpirations = async () => {
        dispatch({ type: REFRESH_STORE_INTEGRATIONS_START });
        try {
            const response = await api.post(
                '/stores/integrations/refresh-expirations',
                {},
                HEADERS()
            );
            dispatch({
                type: REFRESH_STORE_INTEGRATIONS_SUCCESS,
                payload: response.data.data,
            });
            await getStoreIntegrations();
            return response.data.data;
        } catch (error) {
            const errorAction = handleError(error, messageAlertFunction);
            dispatch({
                type: REFRESH_STORE_INTEGRATIONS_ERROR,
                payload: errorAction.payload,
            });
            return null;
        }
    };

    const clearSnackbars = () =>
        dispatch({
            type: CLEAR_SNACKBARS,
        });

    const setSnackbarInterval = () => setTimeout(() => clearSnackbars(), 1000);

    const clearStoreState = () => dispatch({ type: CLEAR_STATE });

    const setLoading = () => dispatch({ type: SET_LOADING });

    return (
        <StoreContext.Provider
            value={{
                ...state,
                getStore,
                createStore,
                deleteStore,
                updateStore,
                clearStoreState,
                setLoading,
                updateAllStores,
                getStoreByConversation,
                getStoreByConversationBDC,
                updateAttStores,
                getStores,
                setCreditCount,
                updateCarouselAgents,
                searchStoresByFilters,
                addSubscription,
                getSubscriptions,
                addSubscriptionCredits,
                getStoreIntegrations,
                refreshStoreIntegrationExpirations,
            }}
        >
            {props.children}
        </StoreContext.Provider>
    );
};

export default StoreState;
